import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import Popup from "reactjs-popup";

import PageSectionMenu from './PageSectionMenu';
import OffersListView from './OffersListView';
import OfferModalApp from '../../OfferModal/startup/OfferModalApp';
import OfferV2ModalApp from '../../OfferV2Modal/startup/OfferV2ModalApp';
import OfferV2PreviewApp from '../../OfferV2Preview/startup/OfferV2PreviewApp';
import AttachArtistToOffer from'../../../shared/AttachArtistToOffer';
import OffersV3MigrationBanner from '../../../shared/OffersV3MigrationBanner';
import useV3OfferMigration from '../../../shared/useV3OfferMigration';
import { attachableLineup, attachPerformerToOffer, needsLineupPerformer } from '../../../shared/offerLineupPerformer';

import {
  FORM_STATE,
  PREVIEW_STATE,
} from '../../OfferModal/constants/offerModalConstants';

import { dealType, guaranteeText, backendText, getV3OfferName, offerDate } from '../utils/offerDealSummary';

const modalContentStyle = {
  "background": "var(--sl-bg-primary)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "800px",
  "padding": "0px"
}

const deleteModalContentStyle = {
  "background": "var(--sl-bg-primary)",
  "textAlign": "left",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "500px",
  "padding": "15px"
}

const ManageOffers = ({
  csrfToken,
  team,
  confirm,
  refreshConfirm,
  currentUser,
  modalOffer,
  defaultOffer,
  offerModalIsOpen,
  closeOfferModal,
  openOfferModal,
  offerTemplates,
  venuesOptions,
  onManageLineupInit,
  fetchOfferData,
  refreshPerformer,
  offerPerformer,
  setOfferPerformer,
  acceptOffer,
  declineOffer,
  offers,
  v3Offers,
  offerChanged,
  updateOfferArtist,
  refreshConfirmOffers,
  refreshConfirmV3Offers,
  deleteOffer,
  venueSelected,
  performers,
  confirmPageSection,
  fetchPermissionSet,
  updateConfirmPageSection,
  venueOwnerships,
  possibleMatchingOffers,
  dismissMatchingOffers,
  acceptMatchingOffer,
  offerV3,
  acceptV3Offer,
  declineV3Offer,
  deleteV3Offer,
  rooms,
  onV3OffersChanged = () => {}
}) => {
  // Route a V3 offer to the editor it was last saved in (O-3412); unset
  // (legacy) falls back to the simple editor.
  const v3OfferEditPath = (o) => {
    const base = (o.editor_variant || 'v3_simple') === 'v3_simple' ? 'offers/v3_simple' : 'offers/v3';
    return `/teams/${team.id}/${base}/${o.id}/edit?from=confirm`;
  };

  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "table";
    return window.localStorage.getItem("sl-offers-view") === "cards" ? "cards" : "table";
  });

  const selectView = (next) => {
    setView(next);
    if (typeof window !== "undefined") window.localStorage.setItem("sl-offers-view", next);
  };

  useEffect(() => {
    onManageLineupInit(defaultOffer);
  }, [])

  const canManage = currentUser.can_manage_offers;

  // Mirror the server gate (pipeline_id required) so we don't show a CTA that 422s.
  const migratedV2Ids = new Set((v3Offers || []).map((o) => o.v2_offer_id).filter(Boolean));
  const hasUnmigratedV2 = (offers || []).some((o) => o.pipeline_id && !migratedV2Ids.has(o.id));
  const hasMigratedOffers = migratedV2Ids.size > 0;
  const showMigration = canManage && offerV3 && team.package?.offer_migration && (hasUnmigratedV2 || hasMigratedOffers);

  const refreshOffers = () => {
    refreshConfirmOffers(team, confirm);
    refreshConfirmV3Offers(team, confirm);
    refreshConfirm(csrfToken, team);
  };

  const { migrating, migrate: onMigrate, undo: onUndoMigrate } = useV3OfferMigration({
    team, calendarEventId: confirm.id, onChange: refreshOffers,
  });

  const attachArtist = (offer) => (
    <Popup
      modal
      contentStyle={deleteModalContentStyle}
      onOpen={(e) => { e.preventDefault(); }}
      trigger={(
        <a href="#" className="sl-offer-attach-artist" onClick={(e) => e.preventDefault()}>
          (Attach artist)
        </a>
      )}
    >
      {close => (
        <AttachArtistToOffer
          csrfToken={csrfToken}
          currentUser={currentUser}
          team={team}
          confirmPerformers={confirm.performers || []}
          offer={offer}
          offerChanged={offerChanged}
          close={close}
          handleAttachArtist={updatedOffer => {
            updateOfferArtist(csrfToken, team, updatedOffer, () => {
              refreshConfirm(csrfToken, team);
              refreshConfirmOffers(team, confirm);
              close();
            });
          }}
        />
      )}
    </Popup>
  );

  const lineup = confirm.performers || [];

  const attachPerformer = (offer, performer) => (
    attachPerformerToOffer(team.id, offer.id, performer.id)
      .then(refreshOffers)
      .catch(() => toast.error(`Couldn't attach ${performer.artistable?.name} to the offer. Please try again.`))
  );

  const missingPerformer = (offer) => (
    needsLineupPerformer(offer, lineup)
      ? { performers: attachableLineup(lineup), onAttach: canManage ? (performer) => attachPerformer(offer, performer) : null }
      : null
  );

  // Normalize V2 + V3 offers into the shared row shape; per-kind callbacks wrap
  // the existing Redux action creators. Migrated V2 offers are hidden (kept in
  // the DB) — their V3 offer supersedes them; undo makes them reappear.
  const v2Rows = offers.filter((offer) => !migratedV2Ids.has(offer.id)).map((offer) => ({
    key: `v2-${offer.id}`,
    raw: offer,
    name: offer.artist_or_event_name,
    isV3: false,
    artistExtra: !offer.primary_performer && !missingPerformer(offer)?.performers.length ? attachArtist(offer) : null,
    missingPerformer: missingPerformer(offer),
    state: offer.state,
    type: dealType(offer),
    guarantee: guaranteeText(offer),
    backend: backendText(offer),
    created: offerDate(offer),
    onAccept: () => acceptOffer(team, offer.id, csrfToken),
    onDecline: () => declineOffer(team, offer.id, csrfToken),
    onDelete: () => deleteOffer(team, offer.id, csrfToken),
    onPreview: () => fetchOfferData(team, offer.id, PREVIEW_STATE),
    onEdit: () => {
      if (offer.ui_version === 1) {
        fetchOfferData(team, offer.id, FORM_STATE);
      } else {
        window.location.href = `/teams/${team.id}/offers/${offer.id}/edit`;
      }
    },
    downloadHref: `/teams/${team.id}/offers/${offer.id}/preview.pdf`,
  }));

  const thenRefreshV3Offers = (request) => Promise.resolve(request)
    .then(() => Promise.all([refreshConfirmV3Offers(team, confirm), onV3OffersChanged()]));

  const v3Rows = v3Offers.map((offer) => ({
    key: `v3-${offer.id}`,
    raw: offer,
    name: offer.artist_or_event_name || getV3OfferName(offer),
    isV3: true,
    artistExtra: null,
    state: offer.state,
    type: dealType(offer),
    guarantee: guaranteeText(offer),
    backend: backendText(offer),
    created: offerDate(offer),
    onAccept: () => thenRefreshV3Offers(acceptV3Offer(team, offer.id, csrfToken)),
    onDecline: () => thenRefreshV3Offers(declineV3Offer(team, offer.id, csrfToken)),
    onDelete: () => thenRefreshV3Offers(deleteV3Offer(team, offer.id, csrfToken)),
    onPreview: () => window.open(`/teams/${team.id}/offers/v3/${offer.id}/preview`, '_blank'),
    onEdit: () => { window.location.href = v3OfferEditPath(offer); },
    downloadHref: `/teams/${team.id}/offers/v3/${offer.id}/preview.pdf`,
  }));

  const rows = [...v2Rows, ...v3Rows].sort(
    (a, b) => new Date(a.raw.created_at) - new Date(b.raw.created_at)
  );

  const matchingOffers = possibleMatchingOffers.length > 0 ? (
    <div className="sl-offers-matching">
      <div className="sl-offers-matching-head">
        <div>
          <strong className="sl-offers-matching-title">Possible Matching Offer(s)</strong>
          <p className="sl-offers-matching-sub">
            {`We found these offers that don't have an associated event but may be applicable to this ${confirm.type === 'Confirm' ? 'confirm' : 'hold'}. Add it now to unlock even more capabilities.`}
          </p>
        </div>
        <a
          href="#"
          className="sl-offers-matching-dismiss"
          onClick={(e) => {
            e.preventDefault();
            dismissMatchingOffers(csrfToken, team, confirm, possibleMatchingOffers);
          }}
        >
          <i className="fal fa-times"></i>
        </a>
      </div>
      {possibleMatchingOffers.map((offer, index) => {
        const editUrl = offer.ui_version === 3
          ? v3OfferEditPath(offer)
          : `/teams/${team.id}/offers/${offer.id}/edit`;

        return (
          <div className="sl-offers-matching-row" key={index}>
            <div>
              <p className="sl-offers-matching-name">
                <strong>
                  {offer.artist_or_event_name}
                  <a href={editUrl} target="_blank" rel="noreferrer">
                    <i className="far fa-eye text-primary ml-1"></i>
                  </a>
                </strong>
              </p>
              <p className="sl-offers-matching-meta">{offer.human_metadata}</p>
            </div>
            <a
              href="#"
              className="sl-offers-matching-add"
              onClick={(e) => {
                e.preventDefault();
                const offerArtist = offer.primary_performer.artistable;
                const performer = performers.find((p) => (
                  p.artistable_type === "Artist"
                    && p.artistable
                    && p.artistable.id === offerArtist.id
                ));
                acceptMatchingOffer(csrfToken, team, confirm, offer, performer);
              }}
            >
              <strong>Add Offer</strong>
            </a>
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <div className="card-body">
      <OffersV3MigrationBanner
        show={showMigration}
        mode={hasUnmigratedV2 ? 'migrate' : 'undo'}
        busy={migrating}
        onMigrate={onMigrate}
        onUndo={onUndoMigrate}
      />

      <OffersListView
        rows={rows}
        view={view}
        onSelectView={selectView}
        canManage={canManage}
        onNewOffer={openOfferModal}
        locked={Boolean(confirmPageSection.permission_set_id)}
        headerActions={(
          <PageSectionMenu
            csrfToken={csrfToken}
            team={team}
            confirm={confirm}
            currentUser={currentUser}
            confirmPageSection={confirmPageSection}
            fetchPermissionSet={fetchPermissionSet}
            updateConfirmPageSection={updateConfirmPageSection}
          />
        )}
        footer={matchingOffers}
      />

      <Popup
        open={offerModalIsOpen}
        contentStyle={modalContentStyle}
        closeOnDocumentClick={false}
        className={"offer-modal"}
        onClose={() => {
          closeOfferModal(defaultOffer, null);
          refreshConfirmOffers(team, confirm);
        }}
      >
        {Object.keys(modalOffer).length > 0 && modalOffer.id && modalOffer.ui_version === 1 ? (
          <OfferModalApp currentUser={currentUser}
                         defaultOffer={defaultOffer}
                         offer={modalOffer}
                         csrfToken={csrfToken}
                         closeModal={closeOfferModal}
                         offerTemplates={offerTemplates}
                         venuesOptions={venuesOptions}
                         team={team} />
        ) : Object.keys(modalOffer).length > 0 && modalOffer.id && modalOffer.ui_version === 2 ? (
          <OfferV2PreviewApp offer={modalOffer}
                             currentUser={currentUser}
                             csrfToken={csrfToken}
                             onClose={
                               () => {
                                 closeOfferModal(defaultOffer, null);
                                 refreshConfirmOffers(team, confirm);
                               }
                             }
                             onSuccess={
                               (offer) => {
                                 closeOfferModal(defaultOffer, null);
                                 refreshConfirmOffers(team, confirm);

                                 toast.success("Offer has been sent.", {
                                   position: toast.POSITION.TOP_CENTER,
                                   draggable: false,
                                   closeOnClick: false,
                                   autoClose: 5000,
                                   hideProgressBar: true
                                 });
                               }
                             }
                             team={team} />
        ) : (
          <OfferV2ModalApp currentUser={currentUser}
                           csrfToken={csrfToken}
                           onClose={
                             () => {
                               closeOfferModal(defaultOffer, null);
                             }
                           }
                           venuesOptions={venuesOptions}
                           venueSelected={venueSelected}
                           venueOwnerships={venueOwnerships}
                           rooms={rooms}
                           calendarEvent={confirm}
                           performers={performers}
                           offerV3={offerV3}
                           team={team} />
        )}
      </Popup>
    </div>
  );
};

ManageOffers.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  refreshConfirm: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  modalOffer: PropTypes.object,
  defaultOffer: PropTypes.object,
  offerModalIsOpen: PropTypes.bool,
  closeOfferModal: PropTypes.func.isRequired,
  openOfferModal: PropTypes.func.isRequired,
  offerTemplates: PropTypes.array,
  venuesOptions: PropTypes.array,
  onManageLineupInit: PropTypes.func.isRequired,
  fetchOfferData: PropTypes.func.isRequired,
  refreshPerformer: PropTypes.func.isRequired,
  offerPerformer: PropTypes.object,
  setOfferPerformer: PropTypes.func.isRequired,
  acceptOffer: PropTypes.func.isRequired,
  declineOffer: PropTypes.func.isRequired,
  offers: PropTypes.array,
  v3Offers: PropTypes.array,
  offerChanged: PropTypes.func.isRequired,
  updateOfferArtist: PropTypes.func.isRequired,
  refreshConfirmOffers: PropTypes.func.isRequired,
  refreshConfirmV3Offers: PropTypes.func.isRequired,
  onV3OffersChanged: PropTypes.func,
  deleteOffer: PropTypes.func.isRequired,
  venueSelected: PropTypes.object,
  performers: PropTypes.array,
  confirmPageSection: PropTypes.object,
  fetchPermissionSet: PropTypes.func.isRequired,
  updateConfirmPageSection: PropTypes.func.isRequired,
  venueOwnerships: PropTypes.array,
  rooms: PropTypes.array,
  possibleMatchingOffers: PropTypes.array,
  dismissMatchingOffers: PropTypes.func.isRequired,
  acceptMatchingOffer: PropTypes.func.isRequired,
  offerV3: PropTypes.bool,
  acceptV3Offer: PropTypes.func,
  declineV3Offer: PropTypes.func,
  deleteV3Offer: PropTypes.func
};

export default ManageOffers;
